import type { ReactNode } from 'react';
import { formatDate } from '@/shared/utils/format';
import { money, whole } from '../utils/adsReport';
import { missedCallLines, shortCampaignNames } from '../utils/adsSnapshot';
import type { Section, SnapshotView, WeekFigures, WindowFigures } from '../utils/adsSnapshot';
import styles from './JennySnapshotSections.module.css';

const pct = (n: number | null): string => (n === null ? 'n/a' : `${n}%`);
/** A share to one decimal, so a column reads evenly ("15.0%" beside "10.1%"). */
const pct1 = (n: number | null): string => (n === null ? 'n/a' : `${n.toFixed(1)}%`);
const dollars = (n: number | null): string => (n === null ? 'n/a' : money(n));
// Google's conversions can be fractional (71.5): shown as the snapshot gives them, never rounded.
const count = (n: number | null): string =>
  n === null ? 'n/a' : n.toLocaleString('en-US', { maximumFractionDigits: 2 });
/** A local "HH:mm" as "2:05 PM". */
const clock = (hhmm: string): string => {
  const [h, m] = hhmm.split(':').map(Number);
  if (!Number.isFinite(h) || !Number.isFinite(m)) return hhmm;
  return `${h % 12 === 0 ? 12 : h % 12}:${String(m).padStart(2, '0')} ${h < 12 ? 'AM' : 'PM'}`;
};
const day = (key: string | null): string =>
  key ? formatDate(new Date(`${key}T12:00:00Z`), 'MMM d') : '';

/** The viewer's time zone, labelled ("EDT"), next to a time shown in their local time. */
const zoneLabel = (iso: string): string =>
  new Intl.DateTimeFormat('en-US', { timeZoneName: 'short' })
    .formatToParts(new Date(iso))
    .find((p) => p.type === 'timeZoneName')?.value ?? '';

/** A campaign row with nothing in the window: one line under the table instead of a row of zeros. */
const quietRow = (r: { calls: number; shareOfSpendPercent: number | null }): boolean =>
  r.calls === 0 && (r.shareOfSpendPercent ?? 0) === 0;

/** The header above Jenny's take: which location, which campaigns, which days. */
export function SnapshotHeader({ view }: { view: SnapshotView }) {
  const h = view.header;
  const prior = view.comparability.prior30d;
  return (
    <section className={styles.header} aria-label="What this report covers">
      {view.stale.map((p) => (
        <div key={`stale-${p.label}`} className={styles.notice} role="status">
          {p.label}&apos;s last sync failed, so these figures are from its last successful sync
          {p.lastSyncAt ? ` on ${formatDate(p.lastSyncAt, 'MMM d, h:mm a')}` : ''}.
        </div>
      ))}
      {h.timezoneWarning && (
        <div className={styles.notice} role="status">
          {h.timezoneWarning}
        </div>
      )}
      <div className={styles.headerTitle}>{h.title}</div>
      <div className={styles.headerLine}>
        Last 30 days
        {h.windowStart && h.windowEnd && `, ${day(h.windowStart)} through ${day(h.windowEnd)}`}
        {(h.asOfLocal || h.asOf) && (
          <span className={styles.muted}>
            {' '}
            · data as of{' '}
            {h.asOfLocal ?? `${formatDate(h.asOf!, 'MMM d, h:mm a')} ${zoneLabel(h.asOf!)}`}
          </span>
        )}
      </div>
      {h.platformStatus.length > 0 && (
        <div className={styles.headerLine}>
          {h.platformStatus.map((p, i) => (
            <span key={p.label}>
              {i > 0 && <span className={styles.muted}> · </span>}
              {p.label}: <span className={styles.muted}>{p.status}</span>
            </span>
          ))}
        </div>
      )}
      {h.campaigns.length > 0 && (
        <details className={styles.headerLine}>
          <summary>
            <span className={styles.muted}>Campaigns:</span>{' '}
            {h.campaigns.map((g) => `${g.names.length} ${g.platform}`).join(', ')}
          </summary>
          {h.campaigns.map((g) => (
            <div key={g.platform}>
              {g.platform}: {g.names.join(', ')}
            </div>
          ))}
        </details>
      )}
      {h.locationNotDetermined.length > 0 && (
        <div className={styles.headerLine}>
          <span className={styles.muted}>Location not determined:</span>{' '}
          {h.locationNotDetermined.join(', ')}{' '}
          <span className={styles.muted}>(counted here; the name gives no location)</span>
        </div>
      )}
      {prior && !prior.likeForLike && (
        <div className={styles.headerLine}>
          <span className={styles.muted}>The earlier 30 days are not like-for-like:</span>{' '}
          {prior.why.join('; ')}.
        </div>
      )}
    </section>
  );
}

function WeekCell({ label, now, before }: { label: string; now: string; before: string }) {
  return (
    <div className={styles.weekCell}>
      <div className={styles.weekLabel}>{label}</div>
      <div className={styles.weekValue}>{now}</div>
      <div className={styles.weekNote}>prior 7 days: {before}</div>
    </div>
  );
}

/** Last 7 days against the 7 before, straight from the snapshot. */
export function ThisWeekStrip({ view }: { view: SnapshotView }) {
  if (!view.thisWeek) return null;
  const { last7d: a, prior7d: b } = view.thisWeek;
  const has = (k: keyof WeekFigures) => a[k] !== null;
  const prior = view.comparability.prior7d;
  return (
    <section aria-label="This week">
      <div className={styles.weekTitle}>Last 7 days, against the 7 before</div>
      {prior && !prior.likeForLike && (
        <p className={styles.note}>
          The prior 7 days are not like-for-like: {prior.why.join('; ')}.
        </p>
      )}
      <div className={styles.week}>
        <WeekCell label="Spend" now={dollars(a.spend)} before={dollars(b.spend)} />
        {has('calls') && <WeekCell label="Calls" now={count(a.calls)} before={count(b.calls)} />}
        {has('callsAtLeast30s') && (
          <WeekCell
            label="Calls of 30s or more"
            now={count(a.callsAtLeast30s)}
            before={count(b.callsAtLeast30s)}
          />
        )}
        {has('missed') && (
          <WeekCell label="Missed calls" now={count(a.missed)} before={count(b.missed)} />
        )}
      </div>
    </section>
  );
}

function Block<T>({
  title,
  sub,
  data,
  children,
}: {
  title: string;
  sub?: string;
  data: Section<T>;
  children: (value: T) => ReactNode;
}) {
  return (
    <section className={styles.block} aria-label={title}>
      <h3 className={styles.blockTitle}>{title}</h3>
      {sub && <p className={styles.blockSub}>{sub}</p>}
      {data.available ? (
        children(data.value)
      ) : (
        <p className={styles.unavailable}>{unavailableLine(data.reason)}</p>
      )}
    </section>
  );
}

/** A section that could not be read says so and why: never "none" and never zeros. */
function unavailableLine(reason: string): string {
  return reason === 'not in this report' ? 'Not in this report.' : `Couldn't read this: ${reason}.`;
}

/** Where the ads showed when they showed: above the organic results, and in the very first spot. */
function Facts({ items }: { items: Array<[string, string]> }) {
  return (
    <dl className={styles.facts}>
      {items.map(([value, label]) => (
        <div key={label}>
          <dt>{value}</dt>
          <dd>{label}</dd>
        </div>
      ))}
    </dl>
  );
}

const windowLine = (w: WindowFigures | null): string =>
  w
    ? `${dollars(w.spend)} spend, ${count(w.clicks)} clicks${w.callsAtLeast30s !== null ? `, ${count(w.callsAtLeast30s)} calls of 30s or more` : ''}`
    : 'n/a';

/** The sections of the report the snapshot adds: every figure as the snapshot gives it. */
export function SnapshotSections({ view }: { view: SnapshotView }) {
  return (
    <div className={styles.sections}>
      <Block
        title="Spend and pacing"
        sub="Month to date against the approved budget. The projection is an estimate at this month's pace."
        data={view.pacing}
      >
        {(p) => (
          <Facts
            items={[
              [dollars(p.monthToDateSpend), 'spent this month'],
              [
                p.approvedMonthlyBudget === null
                  ? 'None on file'
                  : dollars(p.approvedMonthlyBudget),
                'approved monthly budget',
              ],
              [
                dollars(p.runRateProjection),
                p.approvedMonthlyBudget === null || p.projectionPercentOfBudget === null
                  ? 'projected for the month (estimate)'
                  : `projected for the month (estimate, ${pct(p.projectionPercentOfBudget)} of budget)`,
              ],
            ]}
          />
        )}
      </Block>

      {view.reach && (
        <Block
          title="Reach"
          sub="Google Search only: times shown, clicks and impression share (weighted across campaigns). Meta is a separate line."
          data={view.reach.share}
        >
          {(s) => {
            const top = view.reach!.topOfPage;
            return (
              <>
                <p className={styles.note}>
                  Google Search: <strong>{count(view.reach!.impressions)}</strong> times shown ·{' '}
                  <strong>{count(view.reach!.clicks)}</strong> clicks
                </p>
                {view.reach!.otherGoogle && (
                  <p className={styles.note}>
                    Other Google campaigns (no impression share):{' '}
                    {count(view.reach!.otherGoogle.impressions)} times shown ·{' '}
                    {count(view.reach!.otherGoogle.clicks)} clicks
                  </p>
                )}
                {view.reach!.meta && (
                  <p className={styles.note}>
                    Meta, separately: {count(view.reach!.meta.impressions)} times shown ·{' '}
                    {count(view.reach!.meta.clicks)} clicks
                  </p>
                )}
                {/* One aligned table: each row is a share of the searches the ads could have shown for, and adds to 100%. */}
                <table className={styles.table}>
                  <thead>
                    <tr>
                      <th />
                      <th className={styles.num}>Shown</th>
                      <th className={styles.num}>Lost to budget</th>
                      <th className={styles.num}>Lost to ad rank</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>All eligible searches</td>
                      <td className={styles.num}>
                        {s.shownUnderTen ? 'under 10%' : pct1(s.impressionSharePercent)}
                      </td>
                      <td className={styles.num}>{pct1(s.lostToBudgetPercent)}</td>
                      <td className={styles.num}>{pct1(s.lostToRankPercent)}</td>
                    </tr>
                    {top.available && (
                      <>
                        <tr>
                          <td>Top of the page</td>
                          <td className={styles.num}>
                            {top.value.topUnderTen ? 'under 10%' : pct1(top.value.topPercent)}
                          </td>
                          <td className={styles.num}>{pct1(top.value.lostTopToBudgetPercent)}</td>
                          <td className={styles.num}>{pct1(top.value.lostTopToRankPercent)}</td>
                        </tr>
                        <tr>
                          <td>Very first spot</td>
                          <td className={styles.num}>
                            {top.value.absoluteTopUnderTen
                              ? 'under 10%'
                              : pct1(top.value.absoluteTopPercent)}
                          </td>
                          <td />
                          <td />
                        </tr>
                      </>
                    )}
                  </tbody>
                </table>
                <p className={styles.note}>
                  Each row is a share of the searches the ads could have shown for, and normally
                  adds up to about 100%.
                  {(s.includesUnderTenPercent ||
                    (top.available && top.value.includesUnderTenPercent)) &&
                    ' Google reports some shown shares only as “under 10%”, counted as 10%, so those shown shares are upper bounds; lost to budget and lost to ad rank are not.'}
                </p>
                {view.reach!.sumCheck.length > 0 && (
                  <ul className={styles.list} aria-label="Where the shares do not add to 100%">
                    {view.reach!.sumCheck.map((line) => (
                      <li key={line}>{line}</li>
                    ))}
                  </ul>
                )}
                {!top.available && (
                  <p className={styles.unavailable}>
                    Top-of-page share: {unavailableLine(top.reason)}
                  </p>
                )}
              </>
            );
          }}
        </Block>
      )}

      <Block
        title="Calls"
        sub="Calls through Google's forwarding numbers. Counts only: no caller numbers."
        data={view.calls}
      >
        {(c) => {
          // Campaign names without what they all repeat ("Mt. Diablo Family Dentist ..."), as the rest of the page shows them.
          const rows = view.campaignCalls.filter((r) => !quietRow(r));
          const shortNames = shortCampaignNames(rows.map((r) => r.name));
          const shortOf = (name: string) =>
            shortNames[rows.findIndex((r) => r.name === name)] ?? name;
          const spendSum = rows.reduce((n, r) => n + (r.spend ?? 0), 0);
          const callsSum = rows.reduce((n, r) => n + r.calls, 0);
          const longSum = rows.reduce((n, r) => n + (r.callsAtLeast30s ?? 0), 0);
          return (
            <>
              <div className={styles.factGroups}>
                <div>
                  <div className={styles.factGroupTitle}>Calls</div>
                  <Facts
                    items={[
                      [count(c.total), 'calls'],
                      [count(c.answered), 'answered'],
                      [count(c.missed), 'missed'],
                    ]}
                  />
                </div>
                <div>
                  <div className={styles.factGroupTitle}>Length</div>
                  <Facts
                    items={[
                      [count(c.atLeast30s), 'lasted 30s or more'],
                      [count(c.atLeast60s), 'lasted 60s or more'],
                      [
                        c.medianDurationSeconds === null ? 'n/a' : `${c.medianDurationSeconds}s`,
                        'median call',
                      ],
                    ]}
                  />
                </div>
              </div>
              {c.missedCalls.length > 0 && (
                <p className={styles.note}>
                  Missed:{' '}
                  {missedCallLines(
                    c.missedCalls,
                    (m) => `${day(m.date)}${m.time ? `, ${clock(m.time)}` : ''}`,
                    shortOf,
                  ).join('; ')}
                  .
                </p>
              )}
              {(c.outsideOfficeHoursPercent !== null || view.adsRun) && (
                <p className={styles.note}>
                  {c.outsideOfficeHoursPercent !== null &&
                    `${c.outsideOfficeHoursPercent}% of calls came outside office hours. `}
                  {view.adsRun && `The ads only run ${view.adsRun} (their ad schedule).`}
                </p>
              )}
              {view.officeHours && view.officeHours.differsFromAdSchedule.length > 0 && (
                <p className={styles.note} role="note">
                  The ad schedule and the office hours disagree:{' '}
                  {view.officeHours.differsFromAdSchedule.join('; ')}. Calls then count as outside
                  office hours.
                  {view.officeHours.source && ` The hours come from ${view.officeHours.source}.`}
                </p>
              )}
              {rows.length > 0 && (
                <table className={styles.table}>
                  <thead>
                    <tr>
                      <th>Campaign</th>
                      <th className={styles.num}>Spend</th>
                      <th className={styles.num}>Calls</th>
                      <th className={styles.num}>30s or more</th>
                      <th className={styles.num}>Cost per 30s+ call</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r, i) => (
                      <tr key={r.name}>
                        <td title={r.name}>{shortNames[i]}</td>
                        <td className={styles.num}>{dollars(r.spend)}</td>
                        <td className={styles.num}>{whole(r.calls)}</td>
                        <td className={styles.num}>{count(r.callsAtLeast30s)}</td>
                        <td className={styles.num}>
                          {r.costPerCallAtLeast30s === null
                            ? 'n/a'
                            : money(r.costPerCallAtLeast30s)}
                        </td>
                      </tr>
                    ))}
                    {rows.length > 1 && (
                      <tr className={styles.totalRow}>
                        <td>Google Ads total</td>
                        <td className={styles.num}>{money(spendSum)}</td>
                        <td className={styles.num}>{whole(callsSum)}</td>
                        <td className={styles.num}>{whole(longSum)}</td>
                        <td className={styles.num}>
                          {longSum > 0 ? money(spendSum / longSum) : 'n/a'}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              )}
              {view.campaignCalls.some(quietRow) && (
                <p className={styles.muted}>
                  No spend or calls in the window:{' '}
                  {view.campaignCalls
                    .filter(quietRow)
                    .map((r) => r.name)
                    .join(', ')}
                  .
                </p>
              )}
            </>
          );
        }}
      </Block>

      <Block
        title="Google's conversions"
        sub="Google's own count under its own settings, kept apart from the calls above."
        data={view.conversions}
      >
        {(c) => (
          <>
            <Facts
              items={[
                [count(c.conversions), 'Conversions'],
                [count(c.allConversions), 'All conversions'],
              ]}
            />
            {c.actions.length > 0 && (
              <ul className={styles.list}>
                {c.actions.map((a) => (
                  <li key={a.name}>
                    {a.name}: {count(a.conversions)} in Conversions, {count(a.allConversions)} in
                    All conversions
                    {a.countedIn === 'all_conversions_only' && ' (a secondary goal)'}
                  </li>
                ))}
              </ul>
            )}
            {c.reconciliation.length > 0 && (
              <div className={styles.explain}>
                <div className={styles.explainTitle}>Why the numbers differ</div>
                {c.reconciliation.map((line) => (
                  <p key={line}>{line}</p>
                ))}
              </div>
            )}
          </>
        )}
      </Block>

      <Block title="Branded vs everything else" data={view.brand}>
        {(b) => (
          <>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th />
                  <th>Spend</th>
                  <th>Share of Google spend</th>
                  <th>Calls</th>
                  <th>30s or more</th>
                </tr>
              </thead>
              <tbody>
                {(
                  [
                    ['Branded', b.branded],
                    ['Everything else', b.everythingElse],
                  ] as const
                ).map(([label, r]) => (
                  <tr key={label}>
                    <td>{label}</td>
                    <td>{dollars(r.spend)}</td>
                    <td>{pct(r.shareOfLocationSpendPercent)}</td>
                    <td>{count(r.calls)}</td>
                    <td>{count(r.callsAtLeast30s)}</td>
                  </tr>
                ))}
                {b.meta && (
                  <tr>
                    <td>Meta (no call tracking)</td>
                    <td>{dollars(b.meta.spend)}</td>
                    <td>–</td>
                    <td>–</td>
                    <td>–</td>
                  </tr>
                )}
              </tbody>
            </table>
            {b.noBrandOnlyCampaign && (
              <p className={styles.note}>
                No brand-only campaign, so the branded row is $0.
                {b.brandSearchesByCampaign.length > 0 &&
                  ` Searches for your name: ${b.brandSearchesByCampaign
                    .map((x) => `${dollars(x.spend)} in ${x.campaign}`)
                    .join('; ')}.`}
              </p>
            )}
            {b.terms.length > 0 && (
              <p className={styles.note}>Brand terms: {b.terms.join(', ')}.</p>
            )}
          </>
        )}
      </Block>

      {view.health && (
        <section className={styles.block} aria-label="Campaign health">
          <h3 className={styles.blockTitle}>Campaign health</h3>
          {!view.health.googleStatus.available && (
            <p className={styles.unavailable}>
              Google&apos;s campaign statuses: {unavailableLine(view.health.googleStatus.reason)}
            </p>
          )}
          {view.health.googleView && <p className={styles.note}>{view.health.googleView}</p>}
          {view.health.onButNoImpressions.length > 0 && (
            <ul className={styles.list} aria-label="On, but shown to no one">
              {view.health.onButNoImpressions.map((c) => (
                <li key={`nb-${c.campaign}`}>
                  {c.campaign} is on but was shown to no one in the window
                  {c.dailyBudget !== null &&
                    ` (${dollars(c.dailyBudget)}/day${c.schedule ? `, runs ${c.schedule}` : ''})`}
                  : {c.reason ?? 'Google gives no reason at the campaign level'}.
                </li>
              ))}
            </ul>
          )}
          {view.health.notServing.length +
            view.health.limitedByBudget.length +
            view.health.disapproved.length +
            view.health.learning.length ===
          0 ? (
            !view.health.googleView &&
            view.health.googleStatus.available &&
            view.health.onButNoImpressions.length === 0 && (
              <p className={styles.note}>
                Google lists every campaign as eligible, with none limited, disapproved or learning.
              </p>
            )
          ) : (
            <ul className={styles.list}>
              {view.health.notServing.map((c) => (
                <li key={`ns-${c.campaign}`}>
                  {c.campaign} is on but not serving
                  {c.reasons.length > 0 ? `: ${c.reasons.join(', ')}` : ''}.
                </li>
              ))}
              {view.health.limitedByBudget.map((c) => (
                <li key={`lb-${c}`}>{c} is limited by budget.</li>
              ))}
              {view.health.disapproved.map((c) => (
                <li key={`d-${c.campaign}`}>
                  {c.campaign} has ads limited or disapproved
                  {c.reasons.length > 0 ? `: ${c.reasons.join(', ')}` : ''}.
                </li>
              ))}
              {view.health.learning.map((c) => (
                <li key={`l-${c}`}>{c} is still learning.</li>
              ))}
            </ul>
          )}
          {view.health.inactiveWithDisapprovedAds.length > 0 && (
            <p className={styles.note}>
              Inactive campaigns with disapproved ads (paused or removed, nothing in the window):{' '}
              {view.health.inactiveWithDisapprovedAds.join(', ')}.
            </p>
          )}
        </section>
      )}

      <Block
        title="Search terms"
        sub="What people typed. Google gives no calls per search term."
        data={view.searchTerms}
      >
        {(s) => (
          <>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Search term</th>
                  <th>Campaign</th>
                  <th>Spend</th>
                  <th>Clicks</th>
                  <th>Conversions</th>
                </tr>
              </thead>
              <tbody>
                {s.top.map((t) => (
                  <tr key={t.term}>
                    <td>{t.term}</td>
                    <td>
                      {t.campaigns.join(', ') || '—'}
                      {t.tag && <span className={styles.muted}> ({t.tag})</span>}
                    </td>
                    <td>{dollars(t.spend)}</td>
                    <td>{count(t.clicks)}</td>
                    <td>{count(t.conversions)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {s.worthALookCount !== null && s.worthALookCount > 0 && (
              <p className={styles.note}>
                Worth a look: {whole(s.worthALookCount)}{' '}
                {s.worthALookCount === 1 ? 'term' : 'terms'} spent {dollars(s.worthALookSpend)} with
                no conversions
                {s.worthALook.length > 0 && ` (${s.worthALook.map((t) => t.term).join(', ')})`}.
              </p>
            )}
            {s.competitorCampaignTerms && (s.competitorCampaignTerms.termCount ?? 0) > 0 && (
              <p className={styles.note}>
                Competitor campaign, as intended: {whole(s.competitorCampaignTerms.termCount ?? 0)}{' '}
                {s.competitorCampaignTerms.termCount === 1 ? 'search' : 'searches'} for other
                practices cost {dollars(s.competitorCampaignTerms.spend)}
                {s.competitorCampaignTerms.top.length > 0 &&
                  ` (such as "${s.competitorCampaignTerms.top[0]}")`}
                . Not counted in &quot;worth a look&quot;.
              </p>
            )}
            {s.spendNotItemized !== null && s.spendNotItemized > 0 && (
              <p className={styles.note}>
                Not listed by search: {dollars(s.spendNotItemized)} of the window&apos;s Google
                spend
                {s.spendOnAllTerms !== null &&
                  `; the searches it does list total ${dollars(s.spendOnAllTerms)}`}
                . Google&apos;s search-term report leaves some clicks out.
              </p>
            )}
            {s.namingOtherLocations &&
              s.namingOtherLocations.termCount !== null &&
              s.namingOtherLocations.termCount > 0 && (
                <p className={styles.note}>
                  Searches naming {s.namingOtherLocations.places.join(', ')}:{' '}
                  {dollars(s.namingOtherLocations.spend)} on{' '}
                  {whole(s.namingOtherLocations.termCount)}{' '}
                  {s.namingOtherLocations.termCount === 1 ? 'search' : 'searches'}
                  {s.namingOtherLocations.topTerms.length > 0 &&
                    ` (such as "${s.namingOtherLocations.topTerms[0]}")`}
                  .
                </p>
              )}
          </>
        )}
      </Block>

      <Block
        title="What changed"
        sub="Changes recorded in Google Ads, with the days before and after side by side. Not cause and effect."
        data={view.whatChanged}
      >
        {(w) =>
          w.changes.length === 0 ? (
            <p className={styles.note}>
              No changes were recorded in the last 30 days.{w.partial && ` Note: ${w.partial}.`}
            </p>
          ) : (
            <>
              {w.cooldowns.length > 0 && (
                <p className={styles.note}>
                  Cooldown:{' '}
                  {w.cooldowns
                    .map(
                      (c) =>
                        `${c.campaign} changed ${c.lastChange ?? ''}${c.by ? ` (${c.by})` : ''}, no further change recommended until ${c.until ?? ''}`,
                    )
                    .join('; ')}
                  . Before and after stay comparable.
                </p>
              )}
              {w.partial && <p className={styles.note}>Note: {w.partial}.</p>}
              {w.throughPracticeNova.length > 0 && (
                <ul className={styles.list}>
                  {w.throughPracticeNova.map((c) => (
                    <li key={`${c.date}-${c.change}`}>
                      <strong>{c.date}</strong> made through PracticeNova: {c.change}
                      {c.undone ? ' (undone since)' : ''}. From {c.cameFrom ?? 'the ads review'}
                      {c.cooldownUntil ? `; cooldown until ${c.cooldownUntil}` : ''}.
                    </li>
                  ))}
                </ul>
              )}
              <ul className={styles.list}>
                {w.changes
                  .filter((c) => c.onPage)
                  .map((c) => (
                    <li key={`${c.date}-${c.time}-${c.campaign}-${c.what}`}>
                      <strong>
                        {day(c.date)}
                        {c.time && `, ${clock(c.time)}${w.timeZone ? ` ${w.timeZone}` : ''}`}
                      </strong>
                      , {c.campaign}: {c.what}
                      {c.from || c.to ? `: ${c.from ?? 'n/a'} → ${c.to ?? 'n/a'}` : ''}
                      {c.by && <span className={styles.muted}> ({c.by})</span>}.
                      {(c.before || c.after) && (
                        <div className={styles.muted}>
                          Before: {windowLine(c.before)}. After: {windowLine(c.after)}.
                        </div>
                      )}
                    </li>
                  ))}
              </ul>
              {w.changes.some((c) => !c.onPage) && (
                <p className={styles.muted}>
                  Showing {w.changes.filter((c) => c.onPage).length} of {w.changes.length} lines:
                  budget and status changes first, then negative keywords. Ask Jenny for the rest.
                </p>
              )}
            </>
          )
        }
      </Block>

      <Block
        title="New patients"
        sub="Counts the practice matched to its own records and sent back. The ad accounts can't see this."
        data={view.outcomes}
      >
        {(o) => (
          <>
            <p className={styles.note}>
              Practice-reported
              {o.periodStart && o.periodEnd && `, ${day(o.periodStart)} to ${day(o.periodEnd)}`}.
            </p>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Campaign</th>
                  <th>New patients</th>
                  <th>Cost per new patient</th>
                </tr>
              </thead>
              <tbody>
                {o.byCampaign.map((r) => (
                  <tr key={r.campaign}>
                    <td>{r.campaign}</td>
                    <td>{count(r.newPatients)}</td>
                    <td>{dollars(r.costPerNewPatient)}</td>
                  </tr>
                ))}
                <tr>
                  <td>All campaigns</td>
                  <td>{count(o.totalNewPatients)}</td>
                  <td>{dollars(o.costPerNewPatient)}</td>
                </tr>
              </tbody>
            </table>
          </>
        )}
      </Block>
    </div>
  );
}
